import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Button, Dropdown, Empty, List, Typography } from 'antd';

import TabLoadState from '../common/TabLoadState';

import RecordBadge from '../common/RecordBadge';
import { LAUNCH_SCOPE_TITLES } from '../common/LaunchButtonRow';
import { buildActionMenu } from '../common/actionMenu';
import { COLLECTION_ICON, KEBAB, PERSON, PLUS } from '../icons';
import { MULTI_ADD_SCOPE } from '../../hooks/useMultiAddForms';
import { canCreateItems, canManage } from '../../utils/permissions';
import { PIN_KIND } from '../../services/favorites';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { normalizeId } from '../../utils/ids';
import { describeSteps, launchMultiAdd, selectMultiAddForms, stepCardsOf } from './MultiAddButtons';
import { confirmDestructive } from '../common/confirmDestructive';

/**
 * The management list of a collection's multi add forms — shared by the Add
 * item settings tab and the Manage… modal off the Add item picker. Each row
 * reads in two tiers: a stack of the form's cards, the form's name and the
 * tier glyph (the same words the launch row uses) on top; the cards it
 * creates beneath them, muted, two lines at most and then an ellipsis. The
 * ⋯ menu keeps its square at the right, in the platform taxonomy: Open ·
 * Edit… · Pin for everyone / Unpin (managers) · Delete….
 *
 * Only the caller's OWN user-tier forms are ever listed, so a user-tier row
 * is always editable; a collection-tier row is editable by managers only.
 * The server re-checks everything. Editing happens on the editor page.
 *
 * `store` is the useMultiAddForms result of the surface that owns the list.
 */

const SCOPE_GLYPHS = { collection: COLLECTION_ICON, user: PERSON };

function ScopeGlyph({ scope }) {
	const Glyph = SCOPE_GLYPHS[scope];
	if (!Glyph) return null;
	return (
		<span
			className={`quick-add-list__scope quick-add-list__scope--${scope}`}
			title={LAUNCH_SCOPE_TITLES[scope]}
			role="img"
			aria-label={scope === MULTI_ADD_SCOPE.COLLECTION ? 'Pinned for everyone' : 'Yours'}
		>
			<Glyph />
		</span>
	);
}

export const multiAddEditorPath = (collectionId, form = null) => (
	form ? `/collections/${normalizeId(collectionId)}/multi-add/${normalizeId(form._id)}/edit` : `/collections/${normalizeId(collectionId)}/multi-add/new`
);

export default function MultiAddList({ collectionId, collection, cardsById, store, onNavigate }) {
	const { modal } = App.useApp();
	const navigate = useNavigate();
	const { forms = [], loading, loadError, reload, remove, promote, demote } = store || {};
	const isManager = canManage(collection);
	const canCreate = canCreateItems(collection);
	const favorites = useOptionalFavorites();
	// A form can go on Home only when this person can launch it (write on
	// every step's card) — the API says the same with a 403.
	const launchableIds = useMemo(
		() => new Set(selectMultiAddForms({ collection, forms, cardsById }).map(entry => normalizeId(entry.form?._id))),
		[collection, forms, cardsById]
	);

	const go = useCallback((path, options) => {
		if (typeof onNavigate === 'function') onNavigate();
		navigate(path, options);
	}, [navigate, onNavigate]);

	const confirmDelete = useCallback((form) => {
		confirmDestructive(modal, {
			title: `Delete "${form?.name}"?`,
			content: form?.scope === MULTI_ADD_SCOPE.COLLECTION
				? 'It disappears for everyone who uses this collection. The items it added stay. History can restore it.'
				: 'Only you can see this multi add, so only you lose it. The items it added stay. History can restore it.',
			okText: 'Delete',
			onOk: () => remove(form),
		});
	}, [modal, remove]);

	const rows = useMemo(() => forms.map(form => {
		const isCollectionTier = form?.scope === MULTI_ADD_SCOPE.COLLECTION;
		return {
			id: normalizeId(form?._id),
			form,
			cards: stepCardsOf(form, cardsById),
			scope: isCollectionTier ? MULTI_ADD_SCOPE.COLLECTION : MULTI_ADD_SCOPE.USER,
			mayEdit: !isCollectionTier || isManager,
		};
	}), [forms, cardsById, isManager]);

	const menuFor = (row) => buildActionMenu([
		{
			key: 'open',
			group: 'navigate',
			glyph: 'open',
			label: 'Open',
			onClick: () => { if (typeof onNavigate === 'function') onNavigate(); launchMultiAdd(navigate, collectionId, row.form); },
		},
		{
			key: 'edit',
			group: 'record',
			glyph: 'edit',
			label: 'Edit…',
			hidden: !row.mayEdit,
			onClick: () => go(multiAddEditorPath(collectionId, row.form)),
		},
		{
			key: 'pin-home',
			group: 'record',
			glyph: 'pin',
			label: favorites?.isPinned?.(PIN_KIND.MULTI_ADD, row.id, collectionId) ? 'Unpin from Home' : 'Pin to Home',
			hidden: !favorites?.togglePin || !launchableIds.has(row.id),
			onClick: () => favorites.togglePin(PIN_KIND.MULTI_ADD, { targetId: row.id, collectionId: normalizeId(collectionId) }),
		},
		{
			key: 'promote',
			group: 'record',
			glyph: 'collection',
			label: 'Pin for everyone',
			hidden: !isManager || row.scope === MULTI_ADD_SCOPE.COLLECTION,
			onClick: () => promote(row.form),
		},
		{
			key: 'demote',
			group: 'record',
			glyph: 'collection',
			label: 'Unpin — return to your own list',
			hidden: !isManager || row.scope !== MULTI_ADD_SCOPE.COLLECTION,
			onClick: () => demote(row.form),
		},
		{
			key: 'delete',
			group: 'destructive',
			glyph: 'delete',
			label: 'Delete…',
			hidden: !row.mayEdit,
			onClick: () => confirmDelete(row.form),
		},
	]);

	// Until a first read has answered, a skeleton: never "No multi adds yet" ahead of it,
	// and a failed read is an error with Retry, not an empty list (§14).
	if (!forms.length && (loading || loadError)) {
		return <TabLoadState loading={loading} error={!loading && loadError} label='multi adds' onRetry={reload} rows={3} />;
	}

	return (
		<div className="quick-add-list multi-add-list">
			{canCreate ? (
				<div className="quick-add-list__actions">
					<Button type="primary" icon={<PLUS />} onClick={() => go(multiAddEditorPath(collectionId))}>
						New multi add…
					</Button>
				</div>
			) : null}

			<List
				bordered
				dataSource={rows}
				rowKey={row => row.id}
				locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No multi adds yet" /> }}
				renderItem={row => {
					const menu = menuFor(row);
					const name = row.form?.name || '(untitled multi add)';
					const cardLine = row.cards.length ? describeSteps(row.form, cardsById) : '(cards unavailable)';
					return (
						<List.Item key={row.id} data-scope={row.scope}>
							<div className="quick-add-list__row">
								<div className="quick-add-list__main">
									<div className="quick-add-list__lead">
										<RecordBadge kind="card" records={row.cards} variant="plain" size="sm" title={name} />
										<ScopeGlyph scope={row.scope} />
									</div>
									<Typography.Text type="secondary" className="quick-add-list__card" title={cardLine}>
										{cardLine}
									</Typography.Text>
								</div>
								{menu ? (
									<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
										<Button icon={<KEBAB />} aria-label={`Actions for ${name}`} />
									</Dropdown>
								) : null}
							</div>
						</List.Item>
					);
				}}
			/>
		</div>
	);
}
